import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useScriptCreation, meditationParts, ZenSettings } from '../../contexts/ScriptCreationContext';
import { useCoins } from '../../contexts/UserContext';
import { zenService } from '../../services/zenService';
import { scriptService } from '../../services/scriptService';
import { parseTextToSegments } from '../../utils/parseTextToSegments';
import {
  getZenPartRange,
  ZEN_SLIDER_STEP,
  ZEN_PART_POPULAR_OPTIONS,
  ZEN_AMBIENCE_OPTIONS,
  ZEN_MUSIC_OPTIONS,
  ZEN_BRAINWAVE_OPTIONS,
  ZEN_WHITE_NOISE_OPTIONS
} from '../../config/zenPartOptions';
import DualRangeSlider from '../../components/common/DualRangeSlider';
import { HelpButton } from '../../components/common/HelpButton';

const Container = styled.div`
  margin: 0 auto;
  max-width: 900px;
`;

const Section = styled.div`
  background-color: #2a2a2a;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  color: #ffffff;
  margin: 0 0 15px 0;
  font-size: 18px;
`;

const Subtitle = styled.p`
  color: #cccccc;
  margin-bottom: 30px;
  font-size: 16px;
`;

const GoalTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid #444;
  border-radius: 8px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 15px;
  font-family: inherit;
  resize: vertical;

  &:focus { outline: none; border-color: #007bff; }
  &::placeholder { color: #888; }
`;

const OptionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionLabel = styled.label`
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;

  &:focus { outline: none; border-color: #007bff; }
`;

const OtherInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;

  &:focus { outline: none; border-color: #007bff; }
  &::placeholder { color: #888; }
`;

const PartCard = styled.div`
  background-color: #1f1f1f;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 15px;
`;

const PartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
`;

const PartTitle = styled.h4`
  color: #ffffff;
  margin: 0;
  font-size: 16px;
`;

const RangeLabel = styled.span`
  color: #7bb8ff;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 8px;
  margin: 14px 0 10px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #cccccc;
  font-size: 14px;
  cursor: pointer;

  input { accent-color: #007bff; }
`;

const CustomInstructions = styled.textarea`
  width: 100%;
  min-height: 44px;
  padding: 10px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-top: 8px;

  &:focus { outline: none; border-color: #007bff; }
  &::placeholder { color: #888; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 25px 0 40px;
  flex-wrap: wrap;
  gap: 15px;
`;

const CostNote = styled.div`
  color: #cccccc;
  font-size: 14px;

  strong { color: #ffd700; }
`;

const GenerateButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { background: #666; cursor: not-allowed; }
`;

const ErrorMessage = styled.div`
  background-color: rgba(220, 53, 69, 0.15);
  border: 1px solid #dc3545;
  color: #ff8b7d;
  border-radius: 8px;
  padding: 12px 15px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const GeneratingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  z-index: 1000;
  color: #ffffff;
  font-size: 18px;
  text-align: center;
  padding: 20px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #444;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin { to { transform: rotate(360deg); } }
`;

const helpContent = `
<div class="alert info">
Intelligent Zen Mode writes your entire meditation for you.
</br></br>
<ul>
<li>Describe the goal of your meditation — the more specific, the better.</li>
<li>Pick a voice and your preferred background sounds ("Auto" lets the AI decide during mixing).</li>
<li>For each part, set how long it should be and optionally pick popular techniques or add instructions.</li>
<li>Generation writes all parts at once, plus a title, description, and artwork prompt.</li>
</ul>
</div>
`;

const ZenOptions: React.FC = () => {
  const navigate = useNavigate();
  const {
    selectedParts,
    customPartNames,
    zenSettings,
    setZenSettings,
    availableVoices,
    selectedVoice,
    handleVoiceChange,
    loadVoices,
    selectedLanguage,
    advancedSettings,
    contentData,
    setContentData,
    editingContent,
    setEditingContent,
    setScriptName,
    setPublishDescription,
    setPublishImagePrompt,
    saveScript,
    scriptDbId,
    segmentsToText,
    playSampleAudio
  } = useScriptCreation();
  const { totalCoins, refreshCoins } = useCoins();

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [pendingMetaSave, setPendingMetaSave] = useState(false);
  const generatedMeta = useRef<{ description: string; imagePrompt: string } | null>(null);

  // Load voices on mount
  useEffect(() => {
    loadVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect back to plan if zen mode wasn't initialized (e.g. direct URL visit)
  useEffect(() => {
    if (!zenSettings && selectedParts.length === 0) {
      navigate('../plan', { replace: true });
    }
  }, [zenSettings, selectedParts.length, navigate]);

  const spokenParts = selectedParts.filter(partId => partId !== 'selfguided');
  const generationCost = spokenParts.length * 30;

  const updateZen = (updates: Partial<ZenSettings>) => {
    if (!zenSettings) return;
    setZenSettings({ ...zenSettings, ...updates });
  };

  const updatePartOption = (partId: string, updates: Partial<ZenSettings['partOptions'][string]>) => {
    if (!zenSettings) return;
    const current = zenSettings.partOptions[partId] || {
      minMinutes: getZenPartRange(partId).min,
      maxMinutes: getZenPartRange(partId).max,
      popularOptions: [],
      customInstructions: ''
    };
    setZenSettings({
      ...zenSettings,
      partOptions: {
        ...zenSettings.partOptions,
        [partId]: { ...current, ...updates }
      }
    });
  };

  const getPartTitle = (partId: string): string => {
    return customPartNames[partId] || meditationParts.find(p => p.id === partId)?.title || partId;
  };

  const handleGenerate = async () => {
    if (!zenSettings) return;
    setError(null);

    if (!zenSettings.goal.trim()) {
      setError('Please describe the goal of your meditation.');
      return;
    }
    if (!selectedVoice || selectedVoice === 'default') {
      setError('Please select a voice.');
      return;
    }
    if (totalCoins < generationCost) {
      setError(`Not enough coins. Generation costs ${generationCost} coins; you have ${totalCoins}.`);
      return;
    }

    setIsGenerating(true);
    try {
      const partsPayload = selectedParts.map(partId => {
        const options = zenSettings.partOptions[partId] || {
          minMinutes: getZenPartRange(partId).min,
          maxMinutes: getZenPartRange(partId).max,
          popularOptions: [],
          customInstructions: ''
        };
        const popularLabels = (ZEN_PART_POPULAR_OPTIONS[partId] || [])
          .filter(opt => options.popularOptions.includes(opt.key))
          .map(opt => opt.label);
        return {
          partId,
          partTitle: getPartTitle(partId),
          minMinutes: options.minMinutes,
          maxMinutes: options.maxMinutes,
          popularOptions: popularLabels,
          customInstructions: options.customInstructions || undefined,
          isSelfGuided: partId === 'selfguided'
        };
      });

      const result = await zenService.generateScript(zenSettings.goal.trim(), selectedLanguage, partsPayload);

      // Apply generated content to context state
      setScriptName(result.title);
      setPublishDescription(result.description);
      setPublishImagePrompt(result.image_prompt);
      generatedMeta.current = { description: result.description, imagePrompt: result.image_prompt };

      const newContentData = { ...contentData };
      const newEditingContent = { ...editingContent };
      result.parts.forEach(({ partId, text }) => {
        const segments = parseTextToSegments(text, partId, 'sentence', advancedSettings);
        newContentData[partId] = segments;
        // Derive editing content from segments so later re-parsing is a no-op
        newEditingContent[partId] = segmentsToText(segments);
      });
      setContentData(newContentData);
      setEditingContent(newEditingContent);
      setZenSettings({ ...zenSettings, stage: 'script_generated' });

      await refreshCoins();

      // Save on the next render so saveScript sees the fresh state
      setPendingSave(true);
    } catch (e: any) {
      console.error('Zen generation failed:', e);
      const data = e?.response?.data;
      setError(data?.error || 'Failed to generate the meditation script. Please try again.');
      setIsGenerating(false);
    }
  };

  // Step 2 of generation: persist the freshly-set state
  useEffect(() => {
    if (!pendingSave) return;
    setPendingSave(false);
    (async () => {
      // saveScript catches its own errors and reports success via its return value
      const saved = await saveScript();
      if (saved) {
        setPendingMetaSave(true);
      } else {
        console.error('Failed to save generated script');
        setError('The script was generated but could not be saved. Use the Save button in the header to retry, then open the Preview step.');
        setIsGenerating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSave]);

  // Step 3: persist description/art prompt to the script row, then continue
  useEffect(() => {
    if (!pendingMetaSave) return;
    if (!scriptDbId) {
      // Save reported success but no script id materialized — bail out of the overlay
      setPendingMetaSave(false);
      setError('The script could not be saved. Please try again.');
      setIsGenerating(false);
      return;
    }
    setPendingMetaSave(false);
    (async () => {
      try {
        if (generatedMeta.current) {
          await scriptService.updateScript(scriptDbId, {
            script_description: generatedMeta.current.description,
            art_prompt: generatedMeta.current.imagePrompt
          } as any);
        }
      } catch (e) {
        console.warn('Could not persist generated metadata (non-fatal):', e);
      }
      setIsGenerating(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('../zen-preview');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMetaSave, scriptDbId]);

  if (!zenSettings) {
    return null;
  }

  return (
    <Container>
      <Subtitle>Tell us about your meditation, and the AI will write the full script for you.</Subtitle>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Section>
        <SectionTitle>Goal of the Meditation</SectionTitle>
        <GoalTextArea
          value={zenSettings.goal}
          onChange={e => updateZen({ goal: e.target.value })}
          placeholder="e.g. Reduce anxiety by observing thoughts without judging them"
          maxLength={500}
        />
      </Section>

      <Section>
        <SectionTitle>Meditation Options</SectionTitle>
        <OptionRow>
          <OptionGroup>
            <OptionLabel htmlFor="zen-voice">Voice</OptionLabel>
            <Select
              id="zen-voice"
              value={selectedVoice === 'default' ? '' : selectedVoice}
              onChange={e => {
                handleVoiceChange(e.target.value);
                const voice = availableVoices.find(v => v.id === e.target.value);
                if (voice?.sampleAudio) playSampleAudio(voice.id);
              }}
            >
              <option value="">Select a voice…</option>
              {availableVoices.map(voice => (
                <option key={voice.id} value={voice.id}>{voice.name}</option>
              ))}
            </Select>
          </OptionGroup>

          <OptionGroup>
            <OptionLabel htmlFor="zen-ambience">Ambience</OptionLabel>
            <Select
              id="zen-ambience"
              value={zenSettings.ambience}
              onChange={e => updateZen({ ambience: e.target.value as ZenSettings['ambience'] })}
            >
              {ZEN_AMBIENCE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
              ))}
            </Select>
            {zenSettings.ambience === 'other' && (
              <OtherInput
                value={zenSettings.ambienceOther || ''}
                onChange={e => updateZen({ ambienceOther: e.target.value })}
                placeholder="Describe the ambience…"
                maxLength={100}
              />
            )}
          </OptionGroup>

          <OptionGroup>
            <OptionLabel htmlFor="zen-music">Music</OptionLabel>
            <Select
              id="zen-music"
              value={zenSettings.music}
              onChange={e => updateZen({ music: e.target.value as ZenSettings['music'] })}
            >
              {ZEN_MUSIC_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
              ))}
            </Select>
            {zenSettings.music === 'other' && (
              <OtherInput
                value={zenSettings.musicOther || ''}
                onChange={e => updateZen({ musicOther: e.target.value })}
                placeholder="Describe the music…"
                maxLength={100}
              />
            )}
          </OptionGroup>

          <OptionGroup>
            <OptionLabel htmlFor="zen-brainwave">Brainwave Entrainment</OptionLabel>
            <Select
              id="zen-brainwave"
              value={zenSettings.brainwave}
              onChange={e => updateZen({ brainwave: e.target.value as ZenSettings['brainwave'] })}
            >
              {ZEN_BRAINWAVE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
              ))}
            </Select>
          </OptionGroup>

          <OptionGroup>
            <OptionLabel htmlFor="zen-noise">White Noise</OptionLabel>
            <Select
              id="zen-noise"
              value={zenSettings.whiteNoise}
              onChange={e => updateZen({ whiteNoise: e.target.value as ZenSettings['whiteNoise'] })}
            >
              {ZEN_WHITE_NOISE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
              ))}
            </Select>
          </OptionGroup>
        </OptionRow>
      </Section>

      <Section>
        <SectionTitle>Meditation Parts</SectionTitle>
        {selectedParts.map(partId => {
          const range = getZenPartRange(partId);
          const options = zenSettings.partOptions[partId] || {
            minMinutes: range.min,
            maxMinutes: range.max,
            popularOptions: [],
            customInstructions: ''
          };
          const popularOptions = ZEN_PART_POPULAR_OPTIONS[partId] || [];

          return (
            <PartCard key={partId}>
              <PartHeader>
                <PartTitle>{getPartTitle(partId)}</PartTitle>
                <RangeLabel>{options.minMinutes}–{options.maxMinutes} min</RangeLabel>
              </PartHeader>

              <DualRangeSlider
                min={0.5}
                max={range.cap}
                step={ZEN_SLIDER_STEP}
                minValue={options.minMinutes}
                maxValue={options.maxMinutes}
                onChange={(minMinutes, maxMinutes) => updatePartOption(partId, { minMinutes, maxMinutes })}
              />

              {popularOptions.length > 0 && (
                <CheckboxGrid>
                  {popularOptions.map(opt => (
                    <CheckboxLabel key={opt.key}>
                      <input
                        type="checkbox"
                        checked={options.popularOptions.includes(opt.key)}
                        onChange={e => {
                          const next = e.target.checked
                            ? [...options.popularOptions, opt.key]
                            : options.popularOptions.filter(k => k !== opt.key);
                          updatePartOption(partId, { popularOptions: next });
                        }}
                      />
                      {opt.label}
                    </CheckboxLabel>
                  ))}
                </CheckboxGrid>
              )}

              {partId !== 'selfguided' && (
                <CustomInstructions
                  value={options.customInstructions}
                  onChange={e => updatePartOption(partId, { customInstructions: e.target.value })}
                  placeholder="Custom instructions for this part (optional)…"
                  maxLength={500}
                />
              )}
            </PartCard>
          );
        })}
      </Section>

      <Footer>
        <CostNote>
          Cost: <strong>{generationCost} coins</strong> ({spokenParts.length} spoken part{spokenParts.length === 1 ? '' : 's'} × 30) — you have {totalCoins}
        </CostNote>
        <GenerateButton onClick={handleGenerate} disabled={isGenerating || !zenSettings.goal.trim()}>
          {isGenerating ? 'Generating…' : '✨ Generate Meditation Script'}
        </GenerateButton>
      </Footer>

      {isGenerating && (
        <GeneratingOverlay>
          <Spinner />
          <div>Writing your meditation…<br /><small>This can take a minute for longer meditations.</small></div>
        </GeneratingOverlay>
      )}

      <HelpButton
        title="Intelligent Zen Mode - Options"
        content={helpContent}
        position="fixed-bottom-right"
      />
    </Container>
  );
};

export default ZenOptions;
